import type {CreateExtArgs, DmApis, Extension, ExtensionAPI, InitializeExtArgs} from '@wix/document-manager-core'
import type {SchemaExtensionAPI} from '../schema/schema'
import type {RMApi} from '../rendererModel'

import type {BuilderTypes, CompRef} from '@wix/document-services-types'
import type {PlatformExtensionAPI} from '../platform'

import {builderTypes} from '../../utils/builderUtils'
import {getComponentType} from '../../utils/dalUtils'
import type {HooksExtensionApi} from '../hooks/hooks'
import {type DataUpdateBeforeEvent, DATA_MODEL_HOOKS} from '../dataModel/hooks'
import {
    transformBuilderDataToFlat,
    transformBuilderDataToNestedObjects,
    BUILDER_COMPONENTS_HOOKS,
    type AppManifestUpdatedEvent
} from './builderComponentsHooks'
import {builderComponentsContainerPopulationHook} from './hooks/autoPopulateAdd'
import {builderComponentsDataUpdateHook} from './hooks/autoPopulateUpdate'

import {type ComponentAfterAddDataEvent, COMPONENTS_HOOKS} from '../components/hooks'
import type {DistributorExtensionAPI} from '../distributor/distributor'
import {MANIFEST_UPDATE_MESSAGE, type SchemaResourceLoaderExtensionAPI} from '../schemaResourceLoader'
import type {ManifestUpdateMessage} from '../distributor/messages'
import {RESOURCE_TYPES} from '../../constants/constants'

interface BuilderComponentsApi extends ExtensionAPI {
    getBuilderTypes(compPointer: CompRef): BuilderTypes
    isBuilder(compType: string): boolean
}

interface BuilderComponentsExtensionAPI extends ExtensionAPI {
    builderComponents: BuilderComponentsApi
}

const createExtension = (): Extension => {
    return {
        name: 'builderComponents',
        dependencies: new Set(['rendererModel', 'serviceTopology', 'manifest']),
        createExtensionAPI(args: CreateExtArgs): BuilderComponentsExtensionAPI {
            const {schemaAPI} = args.extensionAPI as SchemaExtensionAPI & SchemaResourceLoaderExtensionAPI
            const {platform} = args.extensionAPI as PlatformExtensionAPI
            const {dal} = args

            return {
                builderComponents: {
                    getBuilderTypes(compPointer: CompRef): BuilderTypes {
                        const compType = getComponentType(dal, compPointer)
                        return builderTypes(schemaAPI, compType)
                    },
                    isBuilder(compType: string) {
                        return !!platform.getBuilderCompAppData(compType)
                    }
                }
            }
        },

        initializeChannelSubscriptions: async ({extensionAPI, resourceLoader}: DmApis) => {
            const {distributor, rendererModel, hooks} = extensionAPI as DistributorExtensionAPI &
                RMApi &
                HooksExtensionApi
            distributor.subscribeToMessage<ManifestUpdateMessage>(MANIFEST_UPDATE_MESSAGE, async ({data}) => {
                if (!data?.appId) {
                    throw new Error('appId should be provided')
                }
                await rendererModel.refreshClientSpecMap()
                await Promise.all([
                    resourceLoader.load(RESOURCE_TYPES.SCHEMA, [data.appId], true),
                    resourceLoader.load(RESOURCE_TYPES.COMPONENTS, [data.appId], true)
                ])
                hooks.executeHook(
                    BUILDER_COMPONENTS_HOOKS.HOT_RELOAD.APP_MANIFEST_UPDATED.createEvent({
                        appId: data.appId
                    } as AppManifestUpdatedEvent)
                )
                console.log('App was updated via wix cli hot reload')
            })
        },

        initialize: async (extArgs: InitializeExtArgs) => {
            const {extensionAPI} = extArgs
            const {hooks} = extensionAPI as HooksExtensionApi

            hooks.registerHookWithExtensionArgs(
                DATA_MODEL_HOOKS.SERIALIZE.DATA_ITEM_AFTER.id,
                transformBuilderDataToFlat
            )

            hooks.registerHookWithExtensionArgs(
                DATA_MODEL_HOOKS.DESERIALIZE.DATA_ITEM_NEST_BEFORE.id,
                transformBuilderDataToNestedObjects
            )

            hooks.registerHookWithExtensionArgs(
                COMPONENTS_HOOKS.ADD_COMPONENT.AFTER.id,
                (_extArgs, {compToAddPointer}: ComponentAfterAddDataEvent) => {
                    return builderComponentsContainerPopulationHook(extArgs, compToAddPointer)
                }
            )
            hooks.registerHookWithExtensionArgs(
                DATA_MODEL_HOOKS.DATA.UPDATE_BEFORE.id,
                (_extArgs, {componentPointer, dataItem}: DataUpdateBeforeEvent) => {
                    return builderComponentsDataUpdateHook(extArgs, componentPointer, dataItem)
                }
            )
        }
    }
}

export {createExtension, BuilderComponentsApi, BuilderComponentsExtensionAPI}
